import { PRODUCT_CHASSIS_SERIES_STATUS, STATUS_COMMON } from '@/utils/enum';
import ApiService from '../../services/api.service';

const productService = {
  /**
   * Search products
   * @param {Object} params - Search parameters
   * @param {string} params.storeId - Store ID
   * @param {string} params.productName - Search text
   * @param {Array} params.excludeProductIds - Product IDs to exclude
   * @returns {Promise}
   */
  searchProducts(params) {
    return ApiService.query(`products/search`, {
      params,
    });
  },

  /**
   * Get product list
   * @param {Object} params - List parameters
   * @returns {Promise}
   */
  getProducts(params) {
    return ApiService.query(`products`, {
      params,
    });
  },

  /**
   * Get product detail
   * @param {string} id - Product ID
   * @returns {Promise}
   */
  getProductDetail(id) {
    return ApiService.query(`products/${id}`);
  },

  /**
   * Create product
   * @param {Object} data - Product data
   * @returns {Promise}
   */
  createProduct(data) {
    return ApiService.post('products', data);
  },

  /**
   * Update product
   * @param {Object} data - Product data
   * @returns {Promise}
   */
  updateProduct(data) {
    return ApiService.put('products', data);
  },

  /**
   * Delete product
   * @param {string} id - Product ID
   * @returns {Promise}
   */
  deleteProduct(id) {
    return ApiService.delete(`products/${id}`);
  },

  /**
   * Get product stock
   * @param {Object} params - Stock parameters
   * @returns {Promise}
   */
  getProductStock(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/stock?${queryParams}`);
  },

  /**
   * Update product price
   * @param {Object} data - Price data
   * @returns {Promise}
   */
  updatePrice(data) {
    return ApiService.put('products/update-price', data);
  },

  /**
   * Get product types
   * @returns {Promise}
   */
  getProductTypes() {
    return ApiService.query('products/type');
  },
  /**
   * Search products by chassis number
   * @param {Object} params - Search parameters
   * @returns {Promise}
   */
  searchProductsByChassisNumber(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/search/chassis-number?${queryParams}`);
  },

  /**
   * Search products by serial number
   * @param {Object} params - Search parameters
   * @returns {Promise}
   */
  searchProductsBySerialNumber(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/search/serial-number?${queryParams}`);
  },

  /**
   * Get products by chassis number
   * @param {string} storeId - Store ID
   * @param {string} chassisNumber - Chassis number
   * @returns {Promise}
   */
  getProductsByChassisNumber(storeId, chassisNumber) {
    return ApiService.query(
      `products/${storeId}/chassis-product/${chassisNumber}`,
    );
  },

  /**
   * Export products to Excel
   * @param {Object} params - Export parameters
   * @returns {Promise}
   */
  exportExcel(params) {
    const queryParams = new URLSearchParams(params).toString();
    return ApiService.query(`products/export-excel?${queryParams}`, {
      responseType: 'blob',
    });
  },

  /**
   * Preview import Excel
   * @param {File} file - Excel file
   * @returns {Promise}
   */
  previewImportExcel(file) {
    const formData = new FormData();
    formData.append('file', file);
    return ApiService.post('products/import-preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Import Excel
   * @param {Object} data - Import data
   * @returns {Promise}
   */
  importExcel(data) {
    return ApiService.post('products/import', data);
  },

  async getEngineNumberByChassisNumber(
    chassisNumber,
    sourceStoreId = '',
    isExportMode = false,
  ) {
    try {
      const { data } = await ApiService.query(
        `products/${sourceStoreId}/chassis-product/${chassisNumber}`,
      );

      if (
        data.status === STATUS_COMMON.ACTIVE &&
        data.data.length === 1 &&
        data.data[0]?.engineNumber
      ) {
        if (isExportMode) {
          return data.data[0].status === PRODUCT_CHASSIS_SERIES_STATUS.NEW
            ? data.data[0].engineNumber
            : '';
        }

        return data.data[0].engineNumber;
      }

      return '';
    } catch (error) {
      console.error('Error fetching engine number:', error);
      return '';
    }
  },

  updateEngineNumber(payload) {
    return ApiService.patch('products/update-engine-number', payload);
  },
};

export default productService;
